import { useEffect, useRef, useState } from "react";
import { getClaudeStatus, disconnectClaude, MCP_CONNECTOR_URL } from "../../lib/api";

export default function IntegrationsTab({ slug }: { slug: string }) {
  void slug; // status is per-user; slug kept for tab-signature consistency
  const [status, setStatus] = useState<"loading" | "connected" | "disconnected" | "error">("loading");
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = () => {
    setStatus("loading");
    getClaudeStatus()
      .then((s) => setStatus(s.connected ? "connected" : "disconnected"))
      .catch(() => setStatus("error"));
  };
  useEffect(load, []);
  useEffect(() => () => { if (copyTimer.current) clearTimeout(copyTimer.current); }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(MCP_CONNECTOR_URL);
      setCopied(true);
      if (copyTimer.current) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      window.prompt("Copy this URL:", MCP_CONNECTOR_URL);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    setErr(null);
    try {
      await disconnectClaude();
      setStatus("disconnected");
      setConfirming(false);
    } catch {
      setErr("Couldn't disconnect — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <h2 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 500, fontSize: 22, letterSpacing: "-0.012em", color: "var(--ink)", marginBottom: 6 }}>
        Connect to Claude
      </h2>
      <p style={{ fontSize: 14, color: "var(--ink-soft)", marginBottom: 18, maxWidth: "60ch" }}>
        Manage this business from Claude — view your schedule, book and cancel appointments, edit services and settings — by adding zedule as a custom connector in claude.ai. Connecting grants Claude access to your scheduling data.
      </p>

      {/* Status row */}
      <div style={{ marginBottom: 16, fontSize: 14 }}>
        {status === "loading" && <span style={{ color: "var(--ink-soft)" }}>Checking connection…</span>}
        {status === "connected" && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--status-confirmed)", fontWeight: 500 }}>
            ● Connected to Claude
          </span>
        )}
        {status === "disconnected" && <span style={{ color: "var(--ink-soft)" }}>● Not connected</span>}
        {status === "error" && (
          <span style={{ color: "var(--ink-soft)" }}>
            Couldn't check status.{" "}
            <button type="button" className="btn-ghost" style={{ padding: "2px 8px", fontSize: 13 }} onClick={load}>Retry</button>
          </span>
        )}
      </div>

      {/* Connector URL + copy */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, background: "var(--paper-well)", border: "1px solid var(--rule)", borderRadius: 10, padding: "8px 8px 8px 14px", marginBottom: 16, alignItems: "center" }}>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "var(--ink)", overflowWrap: "anywhere" }}>
          {MCP_CONNECTOR_URL}
        </span>
        <button type="button" className="btn-ghost" onClick={copy}>{copied ? "Copied" : "Copy URL"}</button>
      </div>

      {/* Steps */}
      <ol style={{ fontSize: 14, color: "var(--ink-soft)", lineHeight: 1.7, paddingLeft: 20, marginBottom: 18, maxWidth: "60ch" }}>
        <li>In claude.ai, open <strong>Settings → Connectors</strong>.</li>
        <li>Click <strong>Add custom connector</strong>.</li>
        <li>Paste the URL above and continue.</li>
        <li>Sign in with your zedule Google account and approve access.</li>
      </ol>

      {/* Disconnect */}
      {status === "connected" && (
        confirming ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 13, color: "var(--ink-soft)" }}>Disconnect Claude? You'll need to reconnect in claude.ai to use it again.</span>
            <button type="button" className="btn-ghost" disabled={busy} onClick={disconnect}>{busy ? "Disconnecting…" : "Confirm disconnect"}</button>
            <button type="button" className="btn-ghost" disabled={busy} onClick={() => setConfirming(false)}>Cancel</button>
          </div>
        ) : (
          <button type="button" className="btn-ghost" onClick={() => setConfirming(true)}>Disconnect</button>
        )
      )}
      {err && <p style={{ fontSize: 13, color: "var(--status-cancelled)", marginTop: 8 }}>{err}</p>}
    </section>
  );
}
